/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { createTooltip } from './tooltip.js';

/*
 * Glossary terms, marked up in place and given a definition on demand.
 *
 * Matching used XRegExp for its `\p{L}` Unicode property escapes. Those are
 * native now, so the vendored copy is gone along with jQuery UI's tooltip.
 */

/** Escape a term so it is matched literally inside a pattern. */
function escapeForRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Elements whose text must not be rewritten. */
const SKIP_WITHIN = 'a[href], .glossary-term, h1, h2, h3, h4, h5, h6';

export const Glossary = function () {
    const terms = [];
    let tooltip = null;
    let cssOptions = {};

    const self = {};

    /**
     * Load the map's glossary.
     *
     * @param {string} url - The glossary endpoint.
     * @returns {Promise<object>} This glossary.
     */
    self.load = (url) =>
        fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'default',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((data) => {
                (data?.terms || []).forEach((term) => {
                    if (!term) return;
                    terms.push(term);
                    // Alternatives and synonyms share the definition.
                    [...(term.alternatives || []), ...(term.synonyms || [])]
                        .forEach((other) => {
                            terms.push({
                                name: other.name,
                                definition: term.definition,
                            });
                        });
                });
                // Longest first, so "Karte" cannot claim the start of
                // "Kartenpunkt" before the longer term is tried.
                terms.sort(
                    (a, b) => (b.name?.length || 0) - (a.name?.length || 0)
                );
                return self;
            })
            .catch(() => {
                console.error('Failed to load glossary.');
                return self;
            });

    /**
     * Style the definition bubble from the map's design settings.
     *
     * @param {object} options - CSS properties.
     */
    self.setCssOptions = (options) => {
        cssOptions = options || {};
        tooltip?.setStyle(cssOptions);
    };

    /** The terms this glossary knows, longest first. */
    self.terms = terms;

    /**
     * Mark up every glossary term inside `root` and give it a tooltip.
     *
     * @param {HTMLElement} root - The content to scan.
     * @returns {number} How many occurrences were marked up.
     */
    self.linkTerms = (root) => {
        if (!root) return 0;

        let marked = 0;
        terms.forEach((term) => {
            if (!term?.name) return;

            const walker = document.createTreeWalker(
                root,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode: (node) => {
                        if (!(node.nodeValue || '').trim()) {
                            return NodeFilter.FILTER_REJECT;
                        }
                        return node.parentElement?.closest(SKIP_WITHIN)
                            ? NodeFilter.FILTER_REJECT
                            : NodeFilter.FILTER_ACCEPT;
                    },
                }
            );

            const nodes = [];
            while (walker.nextNode()) nodes.push(walker.currentNode);

            // The leading group is the boundary character before the term.
            // It is captured rather than looked behind, so it has to be put
            // back or the replacement eats it.
            const pattern = new RegExp(
                `(^|\\P{L})(${escapeForRegExp(term.name)})(?=\\P{L}|$)`,
                'gu'
            );

            nodes.forEach((node) => {
                const text = node.nodeValue;
                if (!pattern.test(text)) return;
                pattern.lastIndex = 0;

                const fragment = document.createDocumentFragment();
                let cursor = 0;
                for (const match of text.matchAll(pattern)) {
                    const [whole, boundary] = match;
                    const start = match.index + boundary.length;
                    fragment.append(
                        text.slice(cursor, start),
                        termElement(term)
                    );
                    cursor = match.index + whole.length;
                    marked += 1;
                }
                fragment.append(text.slice(cursor));
                node.parentNode.replaceChild(fragment, node);
            });
        });

        if (marked) {
            tooltip = tooltip || createTooltip({ style: cssOptions });
            root.querySelectorAll('.glossary-term').forEach((element) => {
                tooltip.attach(element, element.dataset.definition);
            });
        }
        return marked;
    };

    /** Release the tooltip and its listeners. */
    self.destroy = () => {
        tooltip?.destroy();
        tooltip = null;
    };

    /** The tooltip controller, once terms have been marked up. */
    self.tooltip = () => tooltip;

    function termElement(term) {
        const element = document.createElement('dfn');
        element.className = 'glossary-term';
        element.textContent = term.name;
        // `<dfn>` cannot take focus on its own, which left the definition
        // reachable by hover alone.
        element.tabIndex = 0;
        // The definition rides on a data attribute rather than `title`: a
        // native tooltip would otherwise appear alongside this one.
        element.dataset.definition = term.definition || '';
        return element;
    }

    return self;
};

export default Glossary;
